import {
  type Aabb2,
  type ChunkRectRelation,
  type MarqueeState,
  type Point2,
  type SelectionRef,
  type Store,
  abortMarquee,
  beginMarquee,
  chunkKey,
  inflateAabb,
  isMarqueeLive,
  marqueeRect,
  moveMarquee,
  selectionKey,
  setMarqueeTranslateModifier,
  walkChain,
} from '@frontend/viewer-core';

import { MARQUEE_TOLERANCE_PX } from '../constants';
import { coversWholeLoop } from './coversWholeLoop';
import { createMemoizedRolePolicy } from './rolePolicy';
import type {
  DrawingModel,
  MappingAssignments,
  MarqueeController,
  MarqueePreview,
  SelectionGranularity,
  SelectionManager,
} from './types';

type Dependencies = {
  marqueeStore: Store<MarqueeState | null>;
  /** What the box would collect right now. Live tier — the overlay reads it. */
  previewStore: Store<MarqueePreview | null>;
  model: () => DrawingModel | null;
  getHiddenLayers: () => ReadonlySet<number>;
  /** The mapping in force, for the same role-aware chaining a click uses. */
  getAssignments: () => MappingAssignments | null;
  /**
   * What the box catches, read per query so a granularity switch — or Alt held
   * mid-drag — restyles the live preview rather than waiting for the next drag.
   */
  granularity: () => SelectionGranularity;
  selection: SelectionManager;
  screenToWorld: (screen: Point2) => Point2;
};

/**
 * Maps a screen-space box onto a world-space one.
 *
 * Exact, not approximate: view rotation is restricted to quarter turns, which map
 * axes onto axes, so two opposite corners are enough to recover the world box. A
 * free rotation angle would make this a rotated rectangle and force the query
 * itself to change shape — which is a large part of why rotation is quarter turns.
 */
const screenRectToWorld = (
  rect: Aabb2,
  screenToWorld: (screen: Point2) => Point2,
): Aabb2 => {
  const [x0, y0] = screenToWorld([rect[0], rect[1]]);
  const [x1, y1] = screenToWorld([rect[2], rect[3]]);
  return [
    Math.min(x0, x1),
    Math.min(y0, y1),
    Math.max(x0, x1),
    Math.max(y0, y1),
  ];
};

/** True when two previews would paint the same thing. */
const isSamePreview = (
  a: MarqueePreview | null,
  b: MarqueePreview | null,
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.mode !== b.mode || a.refs.length !== b.refs.length) {
    return false;
  }
  return a.refs.every(
    (ref, index) => selectionKey(ref) === selectionKey(b.refs[index]),
  );
};

/**
 * A stable order for a catch.
 *
 * So that an unchanged catch compares equal regardless of the order the spatial
 * query happened to visit its grid buckets in — which is what keeps the preview
 * from republishing, and the accent buffers from rebuilding, on every pixel of a
 * drag that is collecting the same things.
 */
const sortRefs = (refs: readonly SelectionRef[]): SelectionRef[] =>
  [...refs].sort((a, b) => selectionKey(a).localeCompare(selectionKey(b)));

export const createMarqueeController = ({
  marqueeStore,
  previewStore,
  model,
  getHiddenLayers,
  getAssignments,
  granularity,
  selection,
  screenToWorld,
}: Dependencies): MarqueeController => {
  const getRolePolicy = createMemoizedRolePolicy();

  /**
   * The loops the box has caught, under the rule the gesture locked in.
   *
   * Shared by the live preview and the release, deliberately: a preview computed
   * even slightly differently from the commit would show the user one thing and
   * then do another, which is worse than showing nothing at all.
   */
  const collect = (current: MarqueeState): SelectionRef[] => {
    const drawing = model();
    if (!drawing || current.isAborted || !isMarqueeLive(current)) {
      return [];
    }

    // Inflated in SCREEN space before the conversion, so the forgiveness is the
    // same few pixels at every zoom level and quarter turn rather than a world
    // distance that balloons when zoomed out.
    const rect = screenRectToWorld(
      inflateAabb(marqueeRect(current), MARQUEE_TOLERANCE_PX),
      screenToWorld,
    );
    const hiddenLayers = getHiddenLayers();
    const relations = drawing.index.queryRectChunks(rect, { hiddenLayers });

    /**
     * A right-to-left box takes anything it touches; a left-to-right one takes
     * only what it fully surrounds.
     *
     * Neither takes the shape it is drawn *inside* — that falls out here rather
     * than needing a rule, because a containing outline's chunks are nowhere
     * near the box and so never appear in `relations` at all.
     */
    const isCaught = ({ relation }: ChunkRectRelation) =>
      current.rule === 'crossing' ? true : relation === 'enclosed';

    // Edge granularity: every caught chunk is its own thing, and that is the
    // whole answer.
    if (granularity() === 'edge') {
      return sortRefs(
        [...relations.values()]
          .filter(isCaught)
          .map(({ chunk }) => ({ kind: 'edge' as const, ...chunk })),
      );
    }

    // Loop granularity: the box catches *chains*, so each caught chunk is grown
    // into the chain it belongs to and the rule is re-applied to the whole of it.
    // A chain is the unit the user sees, so half of one must never come back.
    const keyOf = getRolePolicy({
      model: drawing,
      assignments: getAssignments(),
      hiddenLayers,
    }).chainKeyOf;
    const seen = new Set<string>();
    const caught: SelectionRef[] = [];

    relations.forEach(({ chunk }) => {
      if (seen.has(chunkKey(chunk))) {
        return;
      }
      const chain = walkChain(drawing.chunkGraph, chunk, { keyOf });
      chain.forEach((member) => seen.add(chunkKey(member)));

      // Crossing takes a chain any part of which the box reached. Enclosing
      // takes one only if *every* chunk of it is inside — the loop-level query
      // could not answer this, because a chain that stops at a role boundary can
      // be wholly enclosed while the stitched loop around it is merely crossing.
      const isChainCaught =
        current.rule === 'crossing'
          ? chain.some((member) => {
              const at = relations.get(chunkKey(member));
              return at !== undefined && isCaught(at);
            })
          : chain.every(
              (member) =>
                relations.get(chunkKey(member))?.relation === 'enclosed',
            );

      if (!isChainCaught) {
        return;
      }

      // A chain covering one whole stitched loop collapses back to a loop ref,
      // for the same reason a click does: that is what carries area and a count
      // of one, and on an unmapped drawing it is every chain.
      const isWholeLoop = coversWholeLoop(drawing, chain);

      caught.push(
        ...(isWholeLoop
          ? [{ kind: 'loop' as const, loopId: chain[0].loopId }]
          : chain.map((member) => ({ kind: 'edge' as const, ...member }))),
      );
    });

    return sortRefs(caught);
  };

  /**
   * Republishes what the box is about to do.
   *
   * Runs on every pointer move, but only *publishes* when the catch actually
   * changes — sweeping the box across empty space, or growing it within the same
   * set of items, leaves the store untouched and so rebuilds no geometry. The
   * query itself is cheap; rebuilding the accent buffers per pixel would not be.
   */
  const refreshPreview = (current: MarqueeState | null): void => {
    const next: MarqueePreview | null = current
      ? { refs: collect(current), mode: current.mergeMode }
      : null;

    previewStore.setState((previous) =>
      isSamePreview(previous, next) ? previous : next,
    );
  };

  const update = (change: (current: MarqueeState) => MarqueeState): void => {
    marqueeStore.setState((current) => (current ? change(current) : current));
    refreshPreview(marqueeStore.getState());
  };

  const clear = () => {
    marqueeStore.setState(() => null);
    previewStore.setState(() => null);
  };

  return {
    begin(screenPoint, modifiers) {
      marqueeStore.setState(() => beginMarquee(screenPoint, modifiers));
      previewStore.setState(() => null);
    },

    move(screenPoint) {
      update((current) => moveMarquee(current, screenPoint));
    },

    setTranslateModifier(isTranslateModifierHeld) {
      update((current) =>
        setMarqueeTranslateModifier(current, isTranslateModifierHeld),
      );
    },

    abort() {
      update(abortMarquee);
      clear();
    },

    refresh() {
      refreshPreview(marqueeStore.getState());
    },

    isActive: () => marqueeStore.getState() !== null,

    commit() {
      const current = marqueeStore.getState();
      clear();

      if (!current) {
        return;
      }
      const caught = collect(current);

      // A press that never travelled is a click, and the click handler owns it —
      // `collect` returns nothing for it, but a `replace` would still clear the
      // selection, so the gesture has to bow out before that.
      if (current.isAborted || !isMarqueeLive(current)) {
        return;
      }

      if (current.mergeMode === 'replace') {
        // An empty replace box is how a user clears without hunting for blank canvas.
        selection.set(caught);
        return;
      }
      if (caught.length === 0) {
        // A Shift box that caught nothing leaves the selection alone, rather than
        // quietly clearing work the user just did.
        return;
      }
      selection.merge(caught, current.mergeMode);
    },
  };
};
